import ic_downloading from '@material-design-icons/svg/outlined/downloading.svg?raw';
import ic_error from '@material-design-icons/svg/outlined/error.svg?raw';
import ic_arrow_upload_progress from '@material-design-icons/svg/outlined/upload_file.svg?raw';
import { LitElement, PropertyValues, TemplateResult, html, unsafeCSS } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { DataCache } from '../../../data/utils/Cache';
import '../Icon';
import styles from './Avatar.scss?inline';

enum EImageState {
    INITIAL,
    DOWNLOADING,
    LOADED,
    UPLOADING,
    ERROR
}

export enum EAvatarVariant {
    CIRCLE = "circle", // default
    ROUNDED = "rounded",
    SQUARE = "square"
}

const statusIndicatorIconMap = {
    [EImageState.DOWNLOADING]: ic_downloading,
    [EImageState.UPLOADING]: ic_arrow_upload_progress,
    [EImageState.ERROR]: ic_error,
}

const roundedVariants = {
    [EAvatarVariant.CIRCLE]: '50%',
    [EAvatarVariant.ROUNDED]: '15%',
    [EAvatarVariant.SQUARE]: '0%'
}

const FONT_SIZE_SCALING_FACTOR = 0.25; // El tamaño de fuente es aproximadamente el 25% de la diagonal del avatar
const FONT_SIZE_DEFAULT_MIN_PX = 6;
const FONT_SIZE_DEFAULT_MAX_PX = 65;

/**
 * Componente de avatar de usuario.
 * Permite mostrar una imagen de perfil, texto alternativo o un ícono por defecto.
 * Soporta variantes de forma (círculo, redondeado, cuadrado) y tamaños personalizados.
 * También maneja estados de carga y error de la imagen.
 * @customElement 'app-avatar'
 */
@customElement('app-avatar')
export class AvatarComponent extends LitElement {
    // Propiedades sobre la imagen del avatar

    /** Indica si se debe usar caché para la imagen del avatar */
    @property({ type: Boolean, reflect: true }) public k_useCache: boolean = false;
    /**
     * * URL de la imagen del avatar
     * * Base64 data URL (no usa cache)
     * */
    @property({ type: String, reflect: true }) public k_src: string | null = null;
    /** URL de la imagen por defecto para el avatar si no se proporciona una imagen o falla al cargarla */
    @property({ type: String, reflect: true }) public k_defaultAvatarImageURL: string | null = null;
    /** SVG por defecto para el avatar si no se proporciona una imagen o falla al cargarla */
    @property({ type: String, reflect: false }) public k_defaultAvatarSVG: string | null = null;

    // Propiedades para la personalización del avatar

    /** Ancho del avatar */
    @property({ type: String, reflect: true }) public k_width: string | null = null;
    /** Altura del avatar */
    @property({ type: String, reflect: true }) public k_height: string | null = null;
    /** Variante de forma del avatar (círculo, redondeado, cuadrado) */
    @property({ type: String, reflect: true }) public k_variant: EAvatarVariant | null = null;

    // Propiedades para el texto alternativo (alt)

    /** Texto alternativo para la imagen del avatar */
    @property({ type: String, reflect: true }) public k_alt: string | null = null;
    /** Si no se define, se calcula automáticamente */
    @property({ type: Number, reflect: true }) public k_altTextFontSizePx: number | null = null;
    /** Tamaño mínimo del texto alternativo en píxeles */
    @property({ type: Number, reflect: true }) public k_altTextFontSizePxMin: number = FONT_SIZE_DEFAULT_MIN_PX;
    /** Tamaño máximo del texto alternativo en píxeles */
    @property({ type: Number, reflect: true }) public k_altTextFontSizePxMax: number = FONT_SIZE_DEFAULT_MAX_PX;
    /** Factor de escala para calcular el tamaño de fuente del texto alternativo basado en la diagonal del avatar */
    @property({ type: Number, reflect: true }) public k_altTextFontSizeScalingFactor: number = FONT_SIZE_SCALING_FACTOR;

    /** URL real de la imagen cargada */
    @property({ type: String, reflect: false }) private k_realImageURL: string | null = null;

    @state() private imageState: EImageState = EImageState.INITIAL;

    @query('slot[name="k_alt"]') private slotAlt!: HTMLSlotElement;

    static styles = [unsafeCSS(styles)];

    // ********************************************************************
    // Ciclo de vida del componente
    // ********************************************************************

    render() {
        return html`
            <div class="wrapper">
                ${this.renderStatusIndicatorIcon()}
                ${this.renderAltTextShort()}
                ${this.renderAvatarImage()}
                ${this.renderSlotAlt()}
            </div>
        `;
    }

    protected update(changedProperties: PropertyValues): void {
        super.update(changedProperties);

        if (changedProperties.has("k_src")) {
            this.prepareResourceLoadAndResolve();
        }
        if (changedProperties.has("k_useCache")) {
            this.prepareResourceLoadAndResolve();
        }
        if (changedProperties.has("k_variant")) {
            const roundedVal = roundedVariants[this.k_variant || EAvatarVariant.CIRCLE];
            this.style.setProperty('--avatar-border-radius', roundedVal);
        }
        if (changedProperties.has("k_width")) {
            if (this.k_width) {
                this.style.setProperty('--width', this.k_width);
            } else {
                this.style.removeProperty('--width');
            }
            this.style.setProperty('--avatar-alt-text-font-size', this.getFontSizeForAltText());
        }
        if (changedProperties.has("k_height")) {
            if (this.k_height) {
                this.style.setProperty('--height', this.k_height);
            } else {
                this.style.removeProperty('--height');
            }
            this.style.setProperty('--avatar-alt-text-font-size', this.getFontSizeForAltText());
        }
    }

    // ********************************************************************
    // Renderers
    // ********************************************************************

    /**
     * Renderiza el ícono indicador de estado de la imagen (cargando, error, subiendo).
     * Nota: No se muestra si hay imagen o texto alternativo.
     * @returns TemplateResult | null
     */
    private renderStatusIndicatorIcon(): TemplateResult | null {
        if (!this.shouldShowStatusIndicator()) return null;

        const state = this.imageState;
        const icon = statusIndicatorIconMap[state]
        return html`<app-icon
                    class="status-indicator"
                    size="15px"
                    color="var(--color-text-light)"
                    .svg=${icon}
                ></app-icon>`;
    }

    private renderAltTextShort(): TemplateResult | null {
        if (!this.shouldShowAltTextShort()) return null;

        const shortText = AvatarComponent._prepareAltTextShort(this.k_alt);
        return html`<div class="alt-text-short">${shortText}</div>`;
    }

    private renderAvatarImage(): TemplateResult {
        const avatar = this.shouldShowAvatarImage()
            ? html`<img
                class=${"avatar"}
                src="${this.k_realImageURL}"
                @load="${this.handleImageLoad}"
                @error="${this.handleImageError}"
                />`
            : html`${this.renderDefaultIcon()}`;

        return avatar;
    }

    private renderDefaultIcon(): TemplateResult | null {
        if (this.k_defaultAvatarSVG) {
            return html`<div class="default-icon">${unsafeHTML(this.k_defaultAvatarSVG)}</div>`;
        }
        if (this.k_defaultAvatarImageURL) {
            return html`<img class="default-icon" src="${this.k_defaultAvatarImageURL}" />`;
        }
        return null;
    }

    private renderSlotAlt(): TemplateResult | null {
        const visible = this.shouldShowSlotAlt();
        return html`<slot
            name="k_alt"
            style="display: ${visible ? 'block' : 'none'}"
            @slotchange=${this.handleSlotChange}
            ></slot>`;
    }

    // ********************************************************************
    // Evaluators
    // ********************************************************************

    private shouldShowStatusIndicator(): boolean {
        if (!!this.k_alt) return false;
        if (this.shouldShowAvatarImage()) return false;
        if (this.shouldShowSlotAlt()) return false;
        return !!statusIndicatorIconMap[this.imageState];
    }

    private shouldShowAltTextShort(): boolean {
        if (!this.k_alt) return false;
        if (this.shouldShowAvatarImage()) return false;
        return true;
    }

    private shouldShowSlotAlt(): boolean {
        if (!!this.k_alt) return false;
        if (this.shouldShowAvatarImage()) return false;
        // verificar si hay contenido en el slot
        if (this.slotAlt) {
            const assignedNodes = this.slotAlt.assignedNodes({ flatten: true });
            // console.warn('assignedNodes slot alt', this.slotAlt, assignedNodes, assignedNodes.length);
            if (assignedNodes.length > 0) {
                return true;
            }
        }
        return false;
    }

    private shouldShowAvatarImage(): boolean {
        if (!this.k_realImageURL) return false;
        if (this.imageState == EImageState.ERROR) return false;
        return true;
    }

    // private shouldShowDefaultIcon(): boolean {
    //     if (this.shouldShowAvatarImage()) return false;
    //     return !!(this._defaultImageURL || this._defaultImageSVGRaw);
    // }


    // ********************************************************************
    // Handlers
    // ********************************************************************

    private handleImageLoad() {
        // console.log('Imagen cargada correctamente');
        if (this.imageState == EImageState.LOADED) return;
        this.imageState = EImageState.LOADED; // La imagen cargó correctamente
    }

    private handleImageError() {
        // console.warn('Error al cargar la imagen de perfil');
        if (this.imageState == EImageState.ERROR) return;
        this.imageState = EImageState.ERROR; // La imagen falló al cargar
    }

    private handleSlotChange(_: Event) {
        this.requestUpdate();
    }

    // ********************************************************************
    // Public utilities
    // ********************************************************************

    /**
     * Prepara el texto alternativo corto a partir del texto alternativo completo.
     * @returns string | null
     * @example
     *  k_alt = "Kidi Teachers" => "KT"
     *  k_alt = "Kidi" => "Ki"
     *  k_alt = "  " => null
     *  k_alt = null => null
     */
    public static _prepareAltTextShort(_altText: string | null): string | null {
        const altText = _altText?.trim() ?? ""; // this.k_alt.trim();
        if (altText.length === 0) return null;
        let shortText = '';
        const words = altText.split(' ').filter(w => w.length > 0);
        if (words.length >= 2) {
            shortText = words[0][0] + words[1][0];
        } else if (words.length === 1) {
            shortText = words[0].substring(0, 2);
        }
        return shortText;
    }

    public _getWidthValue(): number {
        if (this.k_width?.endsWith("px")) {
            const w = parseFloat(this.k_width);
            return isNaN(w) ? 40 : w;
        }
        const avatarWidth = this.offsetWidth || 40;
        return avatarWidth;
    }

    public _getHeightValue(): number {
        if (this.k_height?.endsWith("px")) {
            const h = parseFloat(this.k_height);
            return isNaN(h) ? 40 : h;
        }
        const avatarHeight = this.offsetHeight || 40;
        return avatarHeight;
    }

    // ********************************************************************
    // Private utilities
    // ********************************************************************

    private prepareResourceLoadAndResolve() {
        if (!this.k_src) {
            this.imageState = EImageState.INITIAL;
        } else {
            this.imageState = EImageState.DOWNLOADING;
        }
        this.k_realImageURL = null;
        this.resolveResourceURL();
    }


    private async resolveResourceURL() {
        if (this["___timeout_last_resolve"]) {
            clearTimeout(this["___timeout_last_resolve"]);
        }
        this["___timeout_last_resolve"] = setTimeout(async () => {
            this["___timeout_last_resolve"] = null;
            const src = this.k_src;

            if (!src) {
                this.k_realImageURL = null;
                return;
            }
            const isBase64DataURL = src.startsWith('data:');
            if (isBase64DataURL) {
                console.warn('AvatarComponent: usando base64 data URL, no se usa cache');
                this.k_realImageURL = src;
                return;
            }
            const isLink = src.startsWith('http://') || src.startsWith('https://');
            if (!isLink) {
                console.warn('AvatarComponent: src no es una URL válida: ', src);
                this.k_realImageURL = null;
                return;
            }
            if (this.k_useCache && DataCache._IsSupported()) {
                const response: Response = await DataCache._GetData(src).catch(() => null);
                if (response && response.ok) {
                    const blob = await response.blob();
                    this.k_realImageURL = URL.createObjectURL(blob);
                } else {
                    this.k_realImageURL = null;
                }
                return;
            }

            this.k_realImageURL = src;
        }, 80);
    }

    /**
    * Calcula el tamaño de fuente para el texto alternativo (alt) basado en el tamaño del avatar.
    * El tamaño de fuente es aproximadamente el {@link FONT_SIZE_SCALING_FACTOR}% de la diagonal del rectángulo
    * definido por _width y _height, con un mínimo de {@link AvatarComponent.k_altTextFontSizePxMin}
    * y un máximo de {@link AvatarComponent.k_altTextFontSizePxMax}.
    *
    * @returns El tamaño de fuente en formato CSS (por ejemplo, "15px").
    */
    private getFontSizeForAltText(): string {
        if (this.k_altTextFontSizePx !== null) {
            return `${this.k_altTextFontSizePx}px`;
        }
        const w = this._getWidthValue();
        const h = this._getHeightValue();
        if (isNaN(w) || isNaN(h)) return '15px';
        const diagonal = Math.sqrt(w * w + h * h);
        const minPx = this.k_altTextFontSizePxMin || FONT_SIZE_DEFAULT_MIN_PX;
        const maxPx = this.k_altTextFontSizePxMax || FONT_SIZE_DEFAULT_MAX_PX;
        const scalingFactor = this.k_altTextFontSizeScalingFactor || FONT_SIZE_SCALING_FACTOR;
        const fontSize = Math.max(minPx, Math.min(maxPx, Math.round(diagonal * scalingFactor))) + 'px';
        return fontSize;
    }
}
